import React, { useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { createControleDossierLot } from "../../services/api";
import { defaultFormData, latestPatientControlDossier, mergeFormData } from "./classic/classicDossierData";
import { VARIANT } from "./classic/classicVariant";
import { renderActiveDossier } from "./classic/views/ActiveDossierView";
import { renderLastDossierPatientModal } from "./classic/views/LastDossierPatientModalView";
import { ControleDialogs, PatientRowHoverStyle } from "./shared/components/ControleDialogs";
import { CreateDossierModal } from "./shared/components/DossierDialogs";
import { CONTROLE_BIS_DOSSIER_TYPE } from "./shared/controleConstants";
import { apiErrorMessage } from "./shared/controleFormat";
import { CHARTER_COLORS as theme } from "./shared/controleTheme";
import { useChronicBilling } from "./shared/hooks/useChronicBilling";
import { useControleDossiers, useSaveShortcut } from "./shared/hooks/useControleDossiers";
import { useControlePatients } from "./shared/hooks/useControlePatients";
import { usePatientControlActions, usePatientControlSummary, usePrintReport } from "./shared/hooks/usePatientControlForm";
import { usePatientEditing } from "./shared/hooks/usePatientEditing";
import { patientControlsForDossier } from "./shared/patientControlRules";
import { buttonBase, controleModalOverlayStyle } from "./shared/styles/layoutStyles";

// Fiches classiques : ni dossiers de production PDA, ni dossiers du Contrôle Bis.
function classicDossiers(dossiers, ehpadId) {
    const controleDossiers = dossiers.filter(dossier => (
        dossier.donnees?.meta?.dossierType !== "PRODUCTION_PDA"
        && dossier.donnees?.meta?.dossierType !== CONTROLE_BIS_DOSSIER_TYPE
    ));
    return ehpadId ? controleDossiers.filter(dossier => String(dossier.ehpadId) === String(ehpadId)) : controleDossiers;
}

function useClassicDossierCreation({ workspace, setError, setActiveControleTab }) {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const createDossier = async (ehpadId, dateProduction) => {
        if (!ehpadId || !dateProduction) {
            setError("Sélectionnez un EHPAD et une date de production.");
            return;
        }
        setError("");
        workspace.setSaving(true);
        try {
            const response = await createControleDossierLot(Number(ehpadId), dateProduction);
            workspace.openDossier(response.data);
            setActiveControleTab("controlePatient");
            await workspace.loadDossiers();
            setShowCreateModal(false);
        } catch (error) {
            setError(apiErrorMessage(error, "Création du dossier impossible."));
        } finally {
            workspace.setSaving(false);
        }
    };
    return {
        showCreateModal,
        openCreateModal: () => {
            setShowCreateModal(true);
            setError("");
        },
        closeCreateModal: () => setShowCreateModal(false),
        createDossier,
    };
}

// État et actions de l'écran Contrôle, exposés aux vues sous forme d'un seul objet.
function useClassicControle() {
    const { user } = useAuth();
    const [activeControleTab, setActiveControleTab] = useState("controlePatient");
    const [lastDossierPatientModal, setLastDossierPatientModal] = useState(null);
    const [error, setError] = useState("");
    const workspace = useControleDossiers({ onError: setError, mergeFormData, defaultFormData });
    const activeControleMode = workspace.selectedEhpad?.controleMode || null;
    const activeDossierEhpadId = workspace.activeDossier?.ehpadId || workspace.selectedEhpadId;
    const controle = useControlePatients({ ehpadId: activeDossierEhpadId, onError: setError });
    const editing = usePatientEditing(controle, { onError: setError });
    const chronic = useChronicBilling(controle, { user, onError: setError });
    const actions = usePatientControlActions(workspace, { user });
    const summary = usePatientControlSummary({
        patients: controle.patients,
        controls: workspace.formData.controlePatients,
        variant: VARIANT,
        controleMode: activeControleMode,
    });
    const report = usePrintReport({ variant: VARIANT, workspace, issues: summary.blockingPatientIssues, remainingPatientCount: summary.remainingPatientCount });
    const creation = useClassicDossierCreation({ workspace, setError, setActiveControleTab });
    const filteredDossiers = useMemo(
        () => classicDossiers(workspace.dossiers, workspace.selectedEhpadId),
        [workspace.dossiers, workspace.selectedEhpadId]
    );

    const openLastDossierPatientModal = patient => {
        if (!patient?.patientId) return;
        const dossier = latestPatientControlDossier(workspace.dossiers, workspace.activeDossier, patient.patientId, activeDossierEhpadId);
        setLastDossierPatientModal({
            patient,
            dossier,
            controls: dossier ? patientControlsForDossier(dossier, patient.patientId) : null,
        });
    };

    const saveDossier = (status = workspace.activeDossier?.status || "BROUILLON") => workspace.saveDossier(status, workspace.formData);
    useSaveShortcut(Boolean(workspace.activeDossier) && !workspace.saving, () => saveDossier("BROUILLON"));

    return {
        ...actions,
        ...summary,
        activeControleMode,
        activeControleTab,
        activeDossierEhpadId,
        chronic,
        controle,
        creation,
        editing,
        error,
        filteredDossiers,
        lastDossierPatientModal,
        openDossier: dossier => {
            workspace.openDossier(dossier);
            setActiveControleTab("controlePatient");
        },
        openLastDossierPatientModal,
        printControlePatientReport: report.print,
        report,
        saveDossier,
        setActiveControleTab,
        setLastDossierPatientModal,
        workspace,
    };
}

export default function Controle({ service = null }) {
    const view = useClassicControle();
    const { chronic, controle, creation, editing, error, report, remainingPatientCount, workspace } = view;

    if (workspace.loading) {
        return <div style={{ padding: "28px", color: theme.muted }}>Chargement du contrôle...</div>;
    }

    return (
        <div style={{ padding: "10px", background: theme.page, minHeight: "100%" }}>
            <PatientRowHoverStyle />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "10px", flexWrap: "wrap" }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: workspace.activeDossier ? "18px" : "22px", color: theme.ink }}>{service?.name || "Contrôle"}</h1>
                    {!workspace.activeDossier && <div style={{ color: theme.muted, fontSize: "13px", marginTop: "3px" }}>
                        Fiches de contrôle PDA par EHPAD et date de production.
                    </div>}
                </div>
                <button type="button" onClick={creation.openCreateModal} style={{ ...buttonBase, background: theme.primary, borderColor: theme.primary, color: "white", fontWeight: 700 }}>
                    Création fiche de contrôle
                </button>
            </div>

            {error && (
                <div style={{ marginBottom: "12px", padding: "10px 12px", background: theme.dangerSoft, color: "#991b1b", border: "1px solid #fecaca", borderRadius: "8px", fontSize: "13px" }}>
                    {error}
                </div>
            )}

            {renderActiveDossier(view)}
            {creation.showCreateModal && (
                <CreateDossierModal
                    ehpads={workspace.ehpads}
                    initialEhpadId={workspace.selectedEhpadId}
                    saving={workspace.saving}
                    onCancel={creation.closeCreateModal}
                    onCreate={creation.createDossier}
                />
            )}
            {renderLastDossierPatientModal(view)}
            <ControleDialogs
                workspace={workspace}
                controle={controle}
                editing={editing}
                chronic={chronic}
                report={report}
                remainingPatientCount={remainingPatientCount}
                overlayStyle={controleModalOverlayStyle}
                idPrefix="controle"
            />
        </div>
    );
}
